import moment from 'moment';
import {
  EPredicateFields,
  EPredicateOperators,
  ESearchPredicateTypes,
} from '@common/definitions';
import type { Predicate } from '@common/types/objects';
import type { PromotionAudience } from '@common/types/promotions';
import type { AudienceEvaluationContext } from './build-audience-context';

const SUPPORTED_ATTRIBUTES = new Set<string>([
  EPredicateFields.NETWORK,
  EPredicateFields.FUNCTION,
  EPredicateFields.USER,
  EPredicateFields.LANGUAGE,
  EPredicateFields.DAYS_IN_SERVICE,
  EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE,
  EPredicateFields.DATE,
]);

function compareNumber(
  actual: number,
  comparison: EPredicateOperators,
  value: number | number[],
): boolean {
  switch (comparison) {
    case EPredicateOperators.GT:
      return actual > (value as number);
    case EPredicateOperators.GTE:
      return actual >= (value as number);
    case EPredicateOperators.LT:
      return actual < (value as number);
    case EPredicateOperators.LTE:
      return actual <= (value as number);
    case EPredicateOperators.EQ:
      return actual === (value as number);
    case EPredicateOperators.BETWEEN: {
      const [min, max] = value as number[];
      return actual >= min && actual <= max;
    }
    default:
      return false;
  }
}

function compareDate(
  actual: moment.Moment,
  comparison: EPredicateOperators,
  value: string | null | (string | null)[],
): boolean {
  if (comparison === EPredicateOperators.BETWEEN) {
    const [start, end] = value as (string | null)[];

    if (start) {
      const startMoment = moment(start);
      if (!startMoment.isValid()) return false;
      if (actual.isBefore(startMoment, 'day')) return false;
    }

    if (end) {
      const endMoment = moment(end);
      if (!endMoment.isValid()) return false;
      if (actual.isAfter(endMoment, 'day')) return false;
    }

    return true;
  }

  if (!value || typeof value !== 'string') return false;

  const target = moment(value);
  if (!target.isValid()) return false;

  switch (comparison) {
    case EPredicateOperators.GT:
      return actual.isAfter(target, 'day');
    case EPredicateOperators.GTE:
      return actual.isSameOrAfter(target, 'day');
    case EPredicateOperators.LT:
      return actual.isBefore(target, 'day');
    case EPredicateOperators.LTE:
      return actual.isSameOrBefore(target, 'day');
    case EPredicateOperators.EQ:
      return actual.isSame(target, 'day');
    default:
      return false;
  }
}

function matchesInOrEq(actualValues: string[], expected: string | string[]): boolean {
  const expectedValues = Array.isArray(expected) ? expected : [expected];
  return expectedValues.some((value) => actualValues.includes(value));
}

function getDaysInService(context: AudienceEvaluationContext, now: moment.Moment): number | null {
  if (!context.activeFrom) return null;

  const start = moment(context.activeFrom);
  if (!start.isValid()) return null;

  return now.diff(start, 'days');
}

function getDaysBeforeOutOfService(context: AudienceEvaluationContext, now: moment.Moment): number | null {
  if (!context.activeUntil) return null;

  const end = moment(context.activeUntil);
  if (!end.isValid()) return null;

  return end.diff(now, 'days');
}

function evaluatePredicate(
  predicate: Predicate,
  context: AudienceEvaluationContext,
  now: moment.Moment,
): boolean {
  if (!SUPPORTED_ATTRIBUTES.has(predicate.attribute)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`Unsupported promotion audience predicate attribute: ${predicate.attribute}`);
    }
    return false;
  }

  switch (predicate.attribute) {
    case EPredicateFields.NETWORK:
      return matchesInOrEq(context.networkIds, predicate.value as string | string[]);

    case EPredicateFields.FUNCTION:
      return matchesInOrEq(context.functionIds, predicate.value as string | string[]);

    case EPredicateFields.USER:
      return context.userId
        ? matchesInOrEq([context.userId], predicate.value as string | string[])
        : false;

    case EPredicateFields.LANGUAGE:
      return context.languageLocale
        ? matchesInOrEq([context.languageLocale], predicate.value as string | string[])
        : false;

    case EPredicateFields.DAYS_IN_SERVICE: {
      const days = getDaysInService(context, now);
      if (days === null) return false;
      return compareNumber(days, predicate.comparison, predicate.value as number | number[]);
    }

    case EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE: {
      const days = getDaysBeforeOutOfService(context, now);
      if (days === null) return false;
      return compareNumber(days, predicate.comparison, predicate.value as number | number[]);
    }

    case EPredicateFields.DATE:
      return compareDate(now, predicate.comparison, predicate.value as string | null | (string | null)[]);

    default:
      return false;
  }
}

export function evaluateAudienceForUser(
  audience: PromotionAudience | undefined,
  context: AudienceEvaluationContext,
  now: moment.Moment = moment(),
): boolean {
  if (!audience?.predicates?.length) {
    return true;
  }

  const results = audience.predicates.map((predicate) => evaluatePredicate(predicate, context, now));

  if (audience.predicate_type === ESearchPredicateTypes.MATCH_ALL) {
    return results.every(Boolean);
  }

  return results.some(Boolean);
}
